import { Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  OptionSelect,
  SearchSelect,
} from '../../../../shared/components/search-select/search-select';
import {
  Infraestructure,
  InfraestructureDTO,
} from '../../../../core/models/infraestructure.interface';
import { Expense } from '../../../../core/models/expense.interface';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { InfraestructureService } from '../../../../core/services/infraestructure/infraestructure-service';
import { LoadingService } from '../../../../core/services/loading/loading';
import { ExpensesService } from '../../../../core/services/expenses/expenses-service';
import { FormUtils } from '../../../../shared/utils';
import { switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatCheckbox } from '@angular/material/checkbox';
import { LocationService } from '../../../../core/services/location/location-service';
import { AddressService } from '../../../../core/services/address/address-service';
import { Location } from '../../../../core/models/location.interface';
import { Address } from '../../../../core/models/address.interface';

@Component({
  selector: 'app-infraestructure-form',
  imports: [
    MatFormField,
    ReactiveFormsModule,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    MatIconModule,
    FormsModule,
    SearchSelect,
    MatCheckbox,
  ],
  templateUrl: './infraestructure-form.html',
  styleUrl: './infraestructure-form.scss',
})
export class InfraestructureForm {
  infraestructure: string | null = null;
  errorMsg: string[] | null = null;
  infraestructureSelected: Infraestructure | null = null;

  expenseSelected: OptionSelect[] = [];
  listExpenses: Expense[] = [];
  selectExpense: OptionSelect | null = null;
  expenseSeleccionado: Expense | null = null;

  locationSelected: OptionSelect[] = [];
  listLocations: Location[] = [];
  selectLocation: OptionSelect | null = null;
  locationSeleccionado: Location | null = null;

  addressSelected: OptionSelect[] = [];
  listAddresses: Address[] = [];
  selectAddress: OptionSelect | null = null;
  addressSeleccionado: Address | null = null;

  private formBuilder = inject(FormBuilder);

  constructor(
    private dialogRef: MatDialogRef<InfraestructureForm>,
    private toastr: ToastrService,
    private infraestructureService: InfraestructureService,
    private loadingService: LoadingService,
    private expenseService: ExpensesService,
    private locationService: LocationService,
    private addressService: AddressService,
  ) {}

  ngOnInit(): void {
    this.fetchInfraestructure();
    this.infraestructureService.asyncErrorValidation$.subscribe((error) => {
      if (error) {
        this.errorMsg = FormUtils.parseErrorMsgs(error);
      }
    });
  }

  private fetchInfraestructure() {
    this.locationService
      .getLocations()
      .pipe(
        switchMap((location) => {
          this.listLocations = location;
          this.locationSelected = this.locationService.getOptionLocations(location);
          return this.addressService.getAddresses();
        }),
        switchMap((addresses) => {
          this.listAddresses = addresses;
          this.addressSelected = this.addressService.getOptionAddress(addresses);
          return this.infraestructureService.selectedInfraestructure$;
        }),
      )
      .subscribe((infraestructure) => {
        if (infraestructure) {
          this.infraestructure = String(infraestructure.id);
          this.infraestructureSelected = infraestructure;
          this.infraestructureForm.patchValue({
            ...this.infraestructureSelected,
            location: this.infraestructureSelected.location.id || 0,
            address: this.infraestructureSelected.address.id || 0,
          });

          const addressOpt = this.addressSelected.find((el) => {
            if (this.infraestructureSelected) {
              return el.value === this.infraestructureSelected.address?.id;
            }
            return false;
          });

          const locationOpt = this.locationSelected.find((el) => {
            if (this.infraestructureSelected) {
              return el.value === this.infraestructureSelected.location?.id;
            }
            return false;
          });

          if (typeof addressOpt !== 'undefined') {
            this.selectAddress = addressOpt;
          }
          if (typeof locationOpt !== 'undefined') {
            this.selectLocation = locationOpt;
          }
        }
      });
  }

  infraestructureForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(256)]],
    type: ['', [Validators.maxLength(256)]],
    description: ['', []],
    status: ['', [Validators.maxLength(256)]],
    availability: [false, []],
    max_capacity: [0, []],
    location: [0, [Validators.required]],
    address: [0, [Validators.required]],
  });

  getAddress(id: number) {
    const index = this.listAddresses.findIndex((address) => address.id === id);
    this.addressSeleccionado = this.listAddresses[index];
  }

  onAddressSelected(address: OptionSelect) {
    this.infraestructureForm.get('address')?.setValue(address.value as number);
    this.getAddress(address.value as number);
  }

  getLocation(id: number) {
    const index = this.listLocations.findIndex((location) => location.id === id);
    this.locationSeleccionado = this.listLocations[index];
  }

  onLocationSelected(location: OptionSelect) {
    this.infraestructureForm.get('location')?.setValue(location.value as number);
    this.getLocation(location.value as number);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  saveInfraestructure() {
    const payload = this.infraestructureForm.value as InfraestructureDTO;
    if (this.infraestructureForm.status === 'VALID' && !this.infraestructure) {
      this.loadingService.show(true);
      this.infraestructureService.createInfraestructure(payload).subscribe({
        next: (infraestructure) => {
          this.loadingService.show(false);
          const msg = `La infraestructura ${infraestructure.name} fue CREADA correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    } else if (this.infraestructureForm.status === 'VALID' && this.infraestructure) {
      this.loadingService.show(true);
      const id = Number(this.infraestructure);
      this.infraestructureService.updateInfraestructure(id, { ...payload, id: id }).subscribe({
        next: (infraestructure) => {
          this.loadingService.show(false);
          const msg = `La infraestructura ${infraestructure.name} fue EDITADA correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }
  }
  getAddressValue() {
    return this.selectAddress;
  }
  getLocationValue() {
    return this.selectLocation;
  }
}
